import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { EmptyState } from '@/components/EmptyState';

interface Review {
  id: string;
  employee_name: string;
  reviewer_name: string;
  review_type: string;
  status: string;
  due_date: string;
  overall_rating?: string;
}

export function ReviewsPage() {
  const [status, setStatus] = useState('');
  const navigate = useNavigate();
  const { data, loading } = useApi<{ reviews: Review[] }>(`reviews?status=${status}`, [status]);

  return (
    <div className="space-y-4">
      <select value={status} onChange={e => setStatus(e.target.value)} className="bg-surface-2 border border-border rounded-lg px-3 py-2 text-sm text-foreground">
        <option value="">All Statuses</option>
        <option value="draft">Draft</option>
        <option value="self_review">Self Review</option>
        <option value="manager_review">Manager Review</option>
        <option value="completed">Completed</option>
      </select>
      {loading ? <div className="text-muted-foreground">Loading...</div> : (
        <DataTable
          columns={[
            { key: 'employee_name', header: 'Employee', render: (r: Review) => <span className="font-medium">{r.employee_name}</span> },
            { key: 'reviewer_name', header: 'Reviewer' },
            { key: 'review_type', header: 'Type', render: (r: Review) => <span className="capitalize">{r.review_type.replace('_', ' ')}</span> },
            { key: 'due_date', header: 'Due Date', render: (r: Review) => r.due_date?.split('T')[0] ?? '' },
            { key: 'overall_rating', header: 'Rating', render: (r: Review) => r.overall_rating ? <span className="capitalize text-xs">{r.overall_rating.replace('_', ' ')}</span> : <span className="text-muted-foreground text-xs">-</span> },
            { key: 'status', header: 'Status', render: (r: Review) => <StatusBadge status={r.status} /> },
          ]}
          data={data?.reviews ?? []}
          onRowClick={(row) => navigate(`/reviews/${row.id}`)}
          emptyNode={
            <EmptyState
              icon={<Star className="w-12 h-12" />}
              title="No reviews yet"
              description="Performance reviews will appear here once created."
            />
          }
        />
      )}
    </div>
  );
}
